import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CircuitBoard,
  ListChecks,
  Radio,
  RefreshCw,
  Search,
  Shuffle,
  Wifi,
  WifiOff,
} from 'lucide-react';
import {
  signalBand,
  signalLevel,
  type Olt,
  type OltCard,
  type Onu,
  type Page,
  type PonPort,
  BULK_ONU_IDS_MAX,
  ONU_MODEL_NONE,
  ONU_MODEL_NONE_LABEL,
  type OnuModelOption,
  onuHealthLabel,
  onuHealthTone,
  type OltVlan,
  type KnownOnuRecord,
} from '@simpleacs/shared';
import { api } from '../lib/api';
import { onuPath } from '../lib/links';
import { authorizePath } from './AuthorizeOnu';
import { formatRelative, formatDateTime } from '../lib/format';
import { useLiveDevices } from '../lib/useLiveDevices';
import { DisabledPill } from '../components/OutOfService';
import { OpticalPowerCard } from './device/OpticalPowerCard';
import { PlacementDialog } from './device/PlacementDialog';
import { OnuMigrationDialog } from './olt/OnuMigrationDialog';
import { useSession } from '../lib/session';
import {
  Button,
  Card,
  EmptyState,
  ErrorNotice,
  Field,
  Input,
  PageHeader,
  Pill,
  SearchSelect,
  Spinner,
  StatCard,
  TBody,
  THead,
  TRow,
  Table,
} from '../components/ui';


/** Columns the ONU list can be ordered by. */
type SortKey =
  | 'number'
  | 'name'
  | 'state'
  | 'serial'
  | 'model'
  | 'location'
  | 'signal'
  | 'mode'
  | 'lastSeen'
  | 'zone'
  | 'splitter'
  | 'added';

/**
 * When this ONU was last heard from, and by which half of the platform.
 *
 * Two different facts, and showing only the first is what produced "working,
 * last seen 19h ago": `lastSeenAt` is advanced by the OLT sweep and ONLY on a
 * read that found the ONU `working`, so a chassis that has not been swept
 * lately makes every ONU on it look stale. `lastInformAt` is the CPE reaching
 * the ACS on its own schedule. The fresher one is the honest answer to "is this
 * thing alive", and naming its source is what stops the number being a mystery.
 */
function lastHeard(o: {
  lastSeenAt?: number | null;
  lastInformAt?: number | null;
}): { at: number | null; via: 'ACS' | 'OLT' | null } {
  const seen = o.lastSeenAt ?? null;
  const informed = o.lastInformAt ?? null;
  if (informed !== null && (seen === null || informed >= seen)) return { at: informed, via: 'ACS' };
  if (seen !== null) return { at: seen, via: 'OLT' };
  return { at: null, via: null };
}

/** The saved default order, chosen in Settings and applied on every visit. */
/** The prompt that replaces a blank placement cell. */
function SetPlacement({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        // The row itself opens the ONU; this is a different intention.
        e.stopPropagation();
        onClick();
      }}
      className="rounded-md border border-dashed border-line px-2 py-0.5 text-[11px] text-faint transition-colors hover:border-primary hover:text-primary"
    >
      Set
    </button>
  );
}

/** Filter on how much light the ONU is getting, not on how to order the list. */
/**
 * `unknown` is a real answer, not a gap: the row filter already compares
 * against `signalLevel`, which returns it for an ONU nothing has measured, so
 * the only thing that was missing was a way to ask for them. The optical card
 * counts them and had nowhere to send the operator who clicked.
 */
type SignalFilter = 'all' | 'low' | 'normal' | 'high' | 'unknown';

/**
 * Management mode, three ways.
 *
 * `tr069_other` is an ONU configured for TR-069 and pointed at an ACS this
 * organisation does not run. It is filterable because it is the set an operator
 * needs to FIND: those ONUs look managed, every TR-069 control is inert on
 * them, and until this existed the only way to spot one was to open it.
 */
/**
 * The management filter, plus the two states that are refinements of TR-069
 * rather than alternatives to it.
 *
 * `tr069_silent` and `tr069_stale` both ARE `tr069` as far as the chassis is
 * concerned — that is the whole difficulty they exist to fix. Keeping them in
 * this one control says so; a separate dropdown would imply an ONU could be
 * one without being the other.
 */
type ModeFilter = 'all' | 'tr069' | 'tr069_silent' | 'tr069_stale' | 'tr069_other' | 'omci';

/**
 * The state to show for a row, falling back to the older question while no
 * chassis read has happened — an unread ONU must not render as OMCI.
 */
function modeOf(o: {
  managementState?: string;
  deviceId: string | null;
  oltAcsMismatch?: boolean;
}): ModeFilter {
  if (o.managementState === 'tr069' || o.managementState === 'tr069_other') return o.managementState;
  // Informing our ACS while the OLT config has no ACS line: TR-069 in fact, so
  // it belongs under TR-069 here — the pill flags the disagreement separately.
  if (o.oltAcsMismatch) return 'tr069';
  if (o.managementState === 'omci') return 'omci';
  return o.deviceId ? 'tr069' : 'omci';
}

/**
 * In or out of service in SimpleACS — a decision somebody made, not a reading.
 *
 * Filterable for the same reason `tr069_other` above is: a disabled ONU looks
 * completely ordinary on this list, `working` state and all, and the only thing
 * that has changed is that no bulk run will ever match it. "Which subscribers
 * are we not touching, and why" is a question with no other answer, and it is
 * asked exactly when a bulk plan comes back one short.
 */
type ServiceFilter = 'all' | 'disabled' | 'enabled';
/** The linked CPE's subscriber WAN: any, or routed with no address (2026-09-20 — see `wanState` on the row). */
type WanFilter = 'all' | 'no_address';

/** Which stat card is selected. Unauthorised is not an inventory state. */
type StateFilter = 'all' | 'online' | 'offline' | 'dormant' | 'unauthorised';

interface UnconfiguredOnu {
  ponPort: string;
  serial: string;
  state: string;
  /** The exact model the chassis reported (Huawei autofind, ZTE C6xx uncfg), when it does. */
  detectedModel?: string;
  /** The ONU types a ZTE chassis defines, most-used first; what the wizard offers there. */
  chassisOnuTypes?: string[];
  suggestedOnuId: number;
  usedIds: number[];
  /**
   * Set when SimpleACS already holds this serial on another slot — so this is
   * a move, not a new subscriber. The row offers "Move here" instead of a
   * plain authorise. Read-only server annotation; null for a genuinely new ONU.
   */
  knownElsewhere: KnownOnuRecord | null;
}

/** Which chassis an awaiting ONU is on — needed once the list spans the fleet. */
interface UnconfiguredRow extends UnconfiguredOnu {
  oltId: string;
  oltName: string;
}

/**
 * ONUs the chassis can see but nobody has configured yet.
 *
 * With one OLT picked this is a single live read, kept fresh so an ONU plugged
 * in now appears without a reload. With none picked it sweeps every OLT — that
 * is a CLI session per chassis, so it is cached rather than put on a timer. An
 * OLT that cannot be reached contributes a warning instead of failing the lot;
 * one unreachable chassis must not hide what the others are waiting on.
 */
function useUnconfigured(oltId: string, olts: Olt[]) {
  /*
    In-service chassis only, in BOTH shapes of the question. This is a live
    CLI read per chassis, so the fleet view was quietly logging into the
    disabled OLTs every couple of minutes — and listing their waiting ONUs in
    a section an operator authorises from. The API refuses them now too; this
    keeps the page from asking a question it knows the answer to, and from
    printing that refusal as a warning on every refresh.
  */
  const targets = (oltId ? olts.filter((o) => o.id === oltId) : olts).filter(
    (o) => !o.disabledAt,
  );
  return useQuery({
    queryKey: ['unconfigured', oltId || 'fleet', targets.map((o) => o.id).join(',')],
    queryFn: async () => {
      const results = await Promise.all(
        targets.map(async (olt) => {
          try {
            const r = await api.get<{ items: UnconfiguredOnu[]; warnings?: string[] }>(
              `/olts/${encodeURIComponent(olt.id)}/unconfigured`,
            );
            return {
              items: r.items.map((i) => ({ ...i, oltId: olt.id, oltName: olt.name })),
              warnings: (r.warnings ?? []).map((w) => `${olt.name}: ${w}`),
            };
          } catch (err) {
            const why = err instanceof Error ? err.message : 'could not be read';
            return { items: [] as UnconfiguredRow[], warnings: [`${olt.name}: ${why}`] };
          }
        }),
      );
      return {
        items: results.flatMap((r) => r.items),
        warnings: results.flatMap((r) => r.warnings),
      };
    },
    enabled: targets.length > 0,
    staleTime: oltId ? 0 : 2 * 60_000,
    refetchInterval: oltId ? 30_000 : false,
    refetchOnWindowFocus: false,
  });
}

/**
 * Every ARRIVAL at /onus reads the URL afresh.
 *
 * The filters below seed from the query string once, in their useState
 * initialisers, and nothing writes back — enough while every link into this
 * page came from another page (the VLANs tab, an ONU's "back to list"), since
 * a route change mounts it anew. Universal search lives in the header, so it
 * is reachable FROM this page: an operator standing on the list who searched
 * an ACS-only device and tapped its /onus?search=<serial> hit stayed on the
 * same mounted page — the address bar changed, the initialisers never re-ran,
 * and the list did not move (review 2026-09-13: the very "tapped the hit,
 * nothing filtered" the link was built to end, now with a URL claiming
 * otherwise). Keyed on the history entry rather than the query string so that
 * re-tapping the SAME hit after typing something else into the box also lands
 * on what the hit says, and so Back and Forward show the list their URL names.
 * A remount is what a link into a list means; its cost is the live-device
 * resubscribe every arrival from another page already pays. Still no URL
 * write-back: the box and the filters live in state until the next arrival.
 */
export function DevicesPage() {
  const location = useLocation();
  return <DevicesPageBody key={location.key} />;
}

function DevicesPageBody() {
  const { activeOrg } = useSession();
  const { connection, lastUpdateAt } = useLiveDevices(activeOrg?.id ?? null);

  // The OLT → card → PON filter chain. An OLT switches the page to that OLT's
  // own inventory (unconfigured ONUs first); with none picked it stays the
  // cross-fleet TR-069 view.
  // Seeded from the URL so a link can land on a filtered view — the VLANs tab
  // links straight to "the ONUs on this VLAN", and arriving at an unfiltered
  // list would make that link a lie.
  const [params] = useSearchParams();
  const [oltId, setOltId] = useState(params.get('oltId') ?? '');
  const [slot, setSlot] = useState(''); // "shelf/slot", e.g. "1/3"
  const [port, setPort] = useState(''); // full PON port, e.g. "1/3/15"
  const [vlan, setVlan] = useState(params.get('vlan') ?? '');
  // Seeded from the URL like the OLT and VLAN above it, and for the same
  // reason: "the HG8145V6s" is a set worth sending somebody a link to. Nothing
  // on this page writes back to the URL — the other filters do not either — so
  // this reads the query string once and then lives in state.
  const [model, setModel] = useState(params.get('model') ?? '');

  const oltsQuery = useQuery({
    queryKey: ['olts'],
    queryFn: () => api.get<Page<Olt>>('/olts'),
  });
  // The OLT's own VLAN table, for the filter below. Only fetched once an OLT
  // is picked: "which VLAN" has no meaning across a fleet where the same
  // number means different things on different chassis.
  const vlanOptions = useQuery({
    queryKey: ['olt', oltId, 'vlans'],
    queryFn: () => api.get<{ items: OltVlan[] }>(`/olts/${encodeURIComponent(oltId)}/vlans`),
    enabled: Boolean(oltId),
  });

  const cardsQuery = useQuery({
    queryKey: ['olt', oltId, 'cards'],
    queryFn: () => api.get<{ items: OltCard[] }>(`/olts/${encodeURIComponent(oltId)}/cards`),
    enabled: Boolean(oltId),
  });
  const portsQuery = useQuery({
    queryKey: ['olt', oltId, 'pon-ports'],
    queryFn: () => api.get<Page<PonPort>>(`/olts/${encodeURIComponent(oltId)}/pon-ports`),
    enabled: Boolean(oltId),
  });

  const olts = oltsQuery.data?.items ?? [];
  const cardSlots = useMemo(() => {
    const set = new Set<string>();
    // Control and uplink boards carry no ONUs, so offering them here only
    // produces a filter that can never match.
    for (const c of cardsQuery.data?.items ?? []) {
      if (c.kind === 'pon' && c.portCount > 0) set.add(`${c.shelf}/${c.slot}`);
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [cardsQuery.data]);
  const cardPorts = useMemo(() => {
    const list = (portsQuery.data?.items ?? [])
      .map((p) => p.port)
      .filter((p) => !slot || p.startsWith(`${slot}/`));
    return list.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  }, [portsQuery.data, slot]);

  // Counted from the OLT inventory, so an ONU that has never spoken TR-069
  // still shows up — the page is about what is on the PON.
  const inventoryQuery = useQuery({
    queryKey: ['onus', oltId || 'fleet'],
    queryFn: () =>
      oltId
        ? api.get<Page<Onu>>(`/olts/${encodeURIComponent(oltId)}/onus`)
        : api.get<Page<Onu>>('/onus'),
    // The state cards follow the chassis: the ONU state poll re-reads every
    // ONU's state every two minutes (onu-state-poll.ts), so a minute is fresh.
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
  const inventory = inventoryQuery.data?.items ?? [];
  // Which slice of the fleet the cards have narrowed to. "unauthorised" is not
  // a state on the inventory — picking it shows only what is awaiting a config.
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [signalFilter, setSignalFilter] = useState<SignalFilter>('all');
  const [modeFilter, setModeFilter] = useState<ModeFilter>('all');
  const navigate = useNavigate();
  // Both directions are offered and neither is the default: a disabled ONU is
  // still a subscriber and belongs in the list, so this narrows rather than
  // hides. `showAbsent` below is the opposite case and stays a checkbox.
  const [serviceFilter, setServiceFilter] = useState<ServiceFilter>('all');
  const [wanFilter, setWanFilter] = useState<WanFilter>('all');
  // Absent ONUs are history, not inventory, so they are off by default — but
  // findable, because "where did that subscriber go" is a real question.
  const [showAbsent, setShowAbsent] = useState(false);

  /**
   * The models this org's ONUs actually report, for the filter below.
   *
   * Read from the fleet rather than derived from the rows on screen: those are
   * already narrowed by the search and the VLAN, so options taken from them
   * would shrink as the operator filtered and "HG8546M" would vanish the moment
   * they searched for a name. Scoped exactly as the list is — the OLT if one is
   * picked, absent ONUs only when they are being shown — so nothing is offered
   * that would come back empty.
   *
   * Cached hard. This groups the whole ONU table for the org; it is a dropdown's
   * worth of data that changes when the inventory sweep runs, not per keystroke.
   */
  const modelOptions = useQuery({
    queryKey: ['onu-models', oltId || 'fleet', showAbsent],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (oltId) qs.set('oltId', oltId);
      if (showAbsent) qs.set('includeRemoved', '1');
      return api.get<{ items: OnuModelOption[] }>(
        `/onus/models${qs.toString() ? `?${qs.toString()}` : ''}`,
      );
    },
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

  /**
   * The dropdown's rows: what is stored, how many wear it, and — where two
   * stored values look like the same hardware written two ways — a note saying
   * so. `546M · also HG8546M` lets an operator SEE that their "all HG8546M"
   * answer is 190 short, without the platform pretending the two strings are
   * one. Nothing is merged; picking either returns only the rows that literally
   * store it.
   *
   * Putting the related name in the HINT rather than only in a tooltip is the
   * load-bearing part: SearchSelect matches typing against the hint as well as
   * the label, so typing `HG8546M` surfaces both rows with both counts — which
   * is the moment the operator finds out the fleet disagrees with itself.
   */
  const modelChoices = useMemo(
    () =>
      (modelOptions.data?.items ?? []).map((o) => ({
        value: o.model ?? ONU_MODEL_NONE,
        label: o.model ?? ONU_MODEL_NONE_LABEL,
        hint:
          o.alsoStoredAs.length > 0
            ? `${o.count} · also ${o.alsoStoredAs.join(', ')}`
            : String(o.count),
      })),
    [modelOptions.data],
  );

  // Waiting on a PON port and not yet authorised. Shared with the list below —
  // same query key, so the chassis is read once and both use the answer.
  const unconfQuery = useUnconfigured(oltId, olts);
  const unconfCount = unconfQuery.data?.items.length ?? 0;

  const counts = useMemo(() => {
    const online = inventory.filter((o) => o.state === 'working');
    // Dormant (down past the operator threshold) is split OUT of offline so the
    // three buckets are mutually exclusive: a long-gone unit is not counted as a
    // fresh outage. `offline` here means recently offline.
    const dormant = inventory.filter((o) => o.dormant);
    const offline = inventory.filter((o) => o.state !== 'working' && !o.dormant);
    /**
     * The follow-up questions, answered on the card instead of by filtering.
     *
     * Of the online ones: how are they managed — through us, through somebody
     * else's ACS (every TR-069 control inert), or OMCI only. Same three-state
     * answer the rows and the filter use, through the same `modeOf`.
     *
     * Of the offline ones: WHY, as far as the chassis said. `dying_gasp` and
     * `power_off` are both the subscriber's power going — the gasp is the ONT
     * announcing it, `power_off` the chassis's own word for it — so they read
     * as one number. `los` is the fibre. Everything else is the chassis
     * saying offline without a cause, and "unknown" is the honest label:
     * inventing a cause here is how somebody rolls a truck to a power cut.
     */
    const mode = { tr069: 0, tr069_other: 0, omci: 0 };
    for (const o of online) mode[modeOf(o) as 'tr069' | 'tr069_other' | 'omci'] += 1;
    const cause = { power: 0, los: 0, unknown: 0 };
    for (const o of offline) {
      if (o.state === 'dying_gasp' || o.state === 'power_off') cause.power += 1;
      else if (o.state === 'los') cause.los += 1;
      else cause.unknown += 1;
    }
    return {
      total: inventory.length,
      online: online.length,
      offline: offline.length,
      dormant: dormant.length,
      /**
       * Coloured the way the rest of the platform colours the same facts —
       * ours is green, somebody else's ACS is the amber the row pills wear,
       * LOS is the red a fibre break earns — and a ZERO stays faint: a loud
       * "0 LOS" is a call-out about nothing, while a quiet one still answers
       * the question. Semibold on the non-zero numbers is what makes the line
       * readable from across the room, which is where these cards are read.
       */
      onlineHint: (
        <span className="flex flex-wrap items-baseline gap-x-1.5 font-medium tabular-nums">
          <span className={mode.tr069 > 0 ? 'text-success font-semibold' : 'text-faint'}>
            {mode.tr069} TR-069
          </span>
          <span className="text-faint">·</span>
          <span className={mode.tr069_other > 0 ? 'text-warn font-semibold' : 'text-faint'}>
            {mode.tr069_other} other ACS
          </span>
          <span className="text-faint">·</span>
          <span className={mode.omci > 0 ? 'text-info font-semibold' : 'text-faint'}>
            {mode.omci} OMCI
          </span>
        </span>
      ),
      offlineHint:
        offline.length > 0 ? (
          <span className="flex flex-wrap items-baseline gap-x-1.5 font-medium tabular-nums">
            <span className={cause.power > 0 ? 'text-warn font-semibold' : 'text-faint'}>
              {cause.power} power cut
            </span>
            <span className="text-faint">·</span>
            <span className={cause.los > 0 ? 'text-danger font-semibold' : 'text-faint'}>
              {cause.los} LOS
            </span>
            <span className="text-faint">·</span>
            <span className={cause.unknown > 0 ? 'text-muted font-semibold' : 'text-faint'}>
              {cause.unknown} unknown
            </span>
          </span>
        ) : undefined,
    };
  }, [inventory]);

  return (
    <>
      <PageHeader
        title="ONUs"
        description={
          activeOrg
            ? `Every ONU the OLTs in ${activeOrg.name} carry, whether or not it speaks TR-069.`
            : 'Every ONU the OLTs carry, whether or not it speaks TR-069.'
        }
        actions={
          <span className="flex items-center gap-2 text-xs text-muted">
            {connection === 'open' ? (
              <Wifi className="size-3.5 text-success" />
            ) : (
              <WifiOff className="size-3.5 text-warn" />
            )}
            {connection === 'open'
              ? `Live · updated ${formatRelative(lastUpdateAt)}`
              : connection === 'connecting'
                ? 'Connecting…'
                : 'Reconnecting…'}
          </span>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <StatCard
          label="Total"
          value={counts.total}
          icon={<Radio className="size-4" />}
          active={stateFilter === 'all'}
          onClick={() => setStateFilter('all')}
        />
        <StatCard
          label="Online"
          value={counts.online}
          tone="success"
          hint={counts.onlineHint}
          active={stateFilter === 'online'}
          onClick={() => setStateFilter('online')}
        />
        <StatCard
          label="Offline"
          value={counts.offline}
          tone={counts.offline ? 'danger' : 'default'}
          hint={counts.offlineHint}
          active={stateFilter === 'offline'}
          onClick={() => setStateFilter('offline')}
        />
        <StatCard
          label="Dormant"
          value={counts.dormant}
          hint="offline past the dormant threshold"
          active={stateFilter === 'dormant'}
          onClick={() => setStateFilter('dormant')}
        />
        <StatCard
          label="Unauthorised"
          value={unconfQuery.isLoading ? '…' : unconfCount}
          tone={unconfCount > 0 ? 'warn' : 'default'}
          hint="waiting on a PON port"
          active={stateFilter === 'unauthorised'}
          onClick={() => setStateFilter('unauthorised')}
        />
      </div>

      {/*
        Light, beside the state counts and before the filter chain: the two
        questions an operator opens this page with are "who is down" and "who
        is about to be", and the second one is optical. It counts the loaded
        inventory — this chassis, or the fleet — so it says the same thing the
        table below it can be filtered to show.
      */}
      <OpticalPowerCard
        onus={inventory}
        scope={oltId ? (olts.find((o) => o.id === oltId)?.name ?? 'this chassis') : 'the fleet'}
        value={signalFilter}
        onChange={setSignalFilter}
      />

      {/* OLT → card → PON filter chain. Picking an OLT switches to its own
          inventory, with unconfigured ONUs listed first. */}
      <Card className="mt-5">
        <div className="flex flex-wrap items-center gap-3 px-4 py-3">
          <SearchSelect
            className="w-52"
            value={oltId}
            onChange={(v) => {
              setOltId(v);
              // VLAN, card and port are all statements about THIS chassis and
              // mean something else on the next one. The model filter is not —
              // an HG8546M is an HG8546M everywhere — so it survives the switch.
              setVlan('');
              setSlot('');
              setPort('');
            }}
            /*
              A disabled chassis is left out: it is not polled, so filtering to
              it shows rows nobody is reading. The ONE exception is a chassis
              already selected — from a saved link, or disabled while this page
              was open — because dropping it then leaves a blank control with a
              filter still applied, which is worse than naming it.
            */
            options={[
              { value: '', label: 'All OLTs' },
              ...olts
                .filter((olt) => !olt.disabledAt || olt.id === oltId)
                .map((olt) => ({
                  value: olt.id,
                  label: olt.name,
                  hint: olt.disabledAt ? 'out of service' : olt.vendor.toUpperCase(),
                })),
            ]}
          />
          <SearchSelect
            className="w-40"
            value={slot}
            disabled={!oltId}
            visibleRows={8}
            onChange={(v) => {
              setSlot(v);
              setPort('');
            }}
            options={[
              { value: '', label: 'All cards' },
              ...cardSlots.map((c) => ({ value: c, label: `Card ${c}` })),
            ]}
          />
          <SearchSelect
            className="w-40"
            value={port}
            disabled={!slot}
            visibleRows={8}
            onChange={setPort}
            options={[
              { value: '', label: 'All PON ports' },
              ...cardPorts.map((c) => ({ value: c, label: c })),
            ]}
          />
          <SearchSelect
            className="w-44"
            value={vlan}
            disabled={!oltId}
            onChange={setVlan}
            title="Only ONUs SimpleACS has recorded on this VLAN"
            options={[
              { value: '', label: oltId ? 'Any VLAN' : 'Any VLAN — pick an OLT' },
              ...(vlanOptions.data?.items ?? []).map((v) => ({
                value: String(v.vlanId),
                label: String(v.vlanId),
                hint: v.description ?? v.name ?? undefined,
              })),
            ]}
          />
          {/* A typeable select rather than a plain one: four models cover most
              of a fleet and belong at the top, but the tail runs to twenty-odd
              values nobody scrolls to. It portals its list, which matters here
              — this filter bar is a Card, and Card is overflow-hidden. */}
          <SearchSelect
            // Same width as its neighbours. It used to be half again as wide,
            // for the sake of the LIST rather than the trigger — a row carries
            // a count and sometimes an "also stored as" note. The list now
            // holds its own minimum width, so the control in the bar does not
            // have to be sized for content it never shows.
            className="w-44"
            value={model}
            onChange={setModel}
            options={modelChoices}
            emptyLabel="Any model"
            placeholder={modelOptions.isLoading ? 'Loading models…' : 'Any model'}
            disabled={modelChoices.length === 0}
          />
          <SearchSelect
            className="w-44"
            value={signalFilter}
            visibleRows={8}
            onChange={(v) => setSignalFilter(v as SignalFilter)}
            title="How much light the ONU is receiving"
            options={[
              { value: 'all', label: 'Any signal' },
              { value: 'low', label: 'Low', hint: 'under -25 dBm' },
              { value: 'normal', label: 'Normal' },
              { value: 'high', label: 'High', hint: 'over -8 dBm' },
              { value: 'unknown', label: 'Not measured' },
            ]}
          />
          <SearchSelect
            className="w-52"
            value={modeFilter}
            visibleRows={8}
            onChange={(v) => setModeFilter(v as ModeFilter)}
            title="Management mode, and — for TR-069 — whether the ONU has actually reached the ACS"
            options={[
              { value: 'all', label: 'Any mode' },
              { value: 'tr069', label: 'TR-069' },
              { value: 'tr069_silent', label: 'TR-069', hint: 'never informed' },
              { value: 'tr069_stale', label: 'TR-069', hint: 'stopped informing' },
              { value: 'tr069_other', label: 'TR-069 (other)' },
              { value: 'omci', label: 'OMCI' },
            ]}
          />
          <SearchSelect
            className="w-52"
            value={wanFilter}
            visibleRows={8}
            onChange={(v) => setWanFilter(v as WanFilter)}
            title="The subscriber WAN as the linked CPE last reported it. 'No address' is a routed WAN that holds no IP — RADIUS rejecting its MAC, DHCP not answering, PPPoE refused: the customer is off."
            options={[
              { value: 'all', label: 'Any WAN' },
              { value: 'no_address', label: 'Routed, no address', hint: 'customer is off' },
            ]}
          />
          <SearchSelect
            className="w-44"
            value={serviceFilter}
            visibleRows={8}
            onChange={(v) => setServiceFilter(v as ServiceFilter)}
            title="Whether an operator has taken the ONU out of service in SimpleACS. It says nothing about the subscriber's connection."
            options={[
              { value: 'all', label: 'In and out of service' },
              { value: 'enabled', label: 'In service only' },
              { value: 'disabled', label: 'Disabled only' },
            ]}
          />
          <label className="flex cursor-pointer items-center gap-2 text-xs text-muted">
            <input
              type="checkbox"
              checked={showAbsent}
              onChange={(e) => setShowAbsent(e.target.checked)}
            />
            Show absent
          </label>
          {oltId && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setOltId('');
                setSlot('');
                setPort('');
              }}
            >
              Clear
            </Button>
          )}
        </div>
      </Card>

      <OltOnuView
        showAbsent={showAbsent}
        oltId={oltId}
        olts={olts}
        oltName={olts.find((o) => o.id === oltId)?.name ?? 'the fleet'}
        slot={slot}
        port={port}
        stateFilter={stateFilter}
        modeFilter={modeFilter}
        serviceFilter={serviceFilter}
        signalFilter={signalFilter}
        vlan={vlan}
        onVlan={setVlan}
        model={model}
        wanFilter={wanFilter}
      />
    </>
  );
}

/**
 * The ONU inventory: the ONUs the chassis has discovered but nobody has
 * authorised are listed FIRST, then the configured ONUs. Both are narrowed by
 * the OLT/card/PON selection — with no OLT picked, both span the whole fleet.
 */
function OltOnuView({
  showAbsent,
  oltId,
  olts,
  oltName,
  slot,
  port,
  stateFilter,
  modeFilter,
  serviceFilter,
  signalFilter,
  vlan,
  onVlan,
  model,
  wanFilter,
}: {
  showAbsent: boolean;
  oltId: string;
  olts: Olt[];
  oltName: string;
  slot: string;
  port: string;
  stateFilter: StateFilter;
  modeFilter: ModeFilter;
  serviceFilter: ServiceFilter;
  signalFilter: SignalFilter;
  vlan: string;
  onVlan: (v: string) => void;
  /** A stored `onu_type` verbatim, or ONU_MODEL_NONE, or '' for any. */
  model: string;
  wanFilter: WanFilter;
}) {
  const navigate = useNavigate();
  const { can } = useSession();
  // The chassis whose ONU-migration dialog is open. A migration moves ONUs
  // onto ONE destination chassis, so this is the OLT the waiting ONU is on —
  // the selected OLT for the header "Move", or the row's own OLT for "Move
  // here" when the list spans the fleet.
  const [migrating, setMigrating] = useState<Olt | null>(null);
  const [placing, setPlacing] = useState<Onu | null>(null);
  // Seeded from the URL like the OLT, VLAN and model filters above, and for
  // the same reason: universal search sends an ACS-only device (one the sweep
  // has not matched to a PON slot) here as /onus?search=<serial>, and until
  // this read that parameter was ignored — the operator tapped a named hit and
  // landed on the unfiltered list (verified live 2026-09-13). Both the draft
  // and the applied term, so the box SHOWS what the list is filtered by and a
  // Clear undoes it. Read once, never written back — nothing on this page is.
  // "Once" is once per ARRIVAL: DevicesPage keys this tree on the history
  // entry, because the header's search can navigate here from here, and a
  // one-time read on a page that stays mounted read nothing at all.
  const [params] = useSearchParams();
  const [draft, setDraft] = useState(params.get('search') ?? '');
  const [search, setSearch] = useState(params.get('search') ?? '');
  // Clicking a column heading sorts by it, and clicking again reverses.
  // Newest first, because the ONU somebody is looking for is almost always one
  // they just authorised. Every column is still sortable from its header.
  const [sortKey, setSortKey] = useState<SortKey>('added');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Same query the Unauthorised card counts, so picking "All OLTs" lists what
  // the whole fleet is waiting on rather than hiding it behind an OLT choice.
  const unconfQuery = useUnconfigured(oltId, olts);
  // No OLT chosen means the whole fleet's inventory, not the ACS cache: an
  // operator thinks in terms of what is on the PON, TR-069 or not.
  // Authorised ONUs come from the stored inventory. Their online/offline state is
  // re-read off the chassis every two minutes by the ONU state poll (the rest by
  // the hourly sweep), so the list re-fetches once a minute while it is open —
  // only while the tab is visible, which is react-query's default.
  const onusQuery = useQuery({
    queryKey: ['onus', oltId || 'fleet', search, showAbsent, vlan, model, wanFilter],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (showAbsent) params.set('includeRemoved', '1');
      // Filtered by the API rather than here: an ONU's VLANs are not on the
      // row, and fetching every ONU to discard most of them is the wrong shape
      // on a fleet this size.
      if (vlan) params.set('vlan', vlan);
      // Server-side too, though `onuType` IS on the row. The read is capped at
      // 5,000 ONUs; filtering after it would apply the model to whatever
      // happened to fit under the cap, and a filtered request is a smaller
      // response than the unfiltered one it replaces.
      if (model) params.set('model', model);
      // Server-side like model: `wan_state` sits on the device row, judged by
      // the sweep, and the read is capped — a filter applied after the cap
      // would miss the customers it exists to find.
      if (wanFilter !== 'all') params.set('wan', wanFilter);
      const qs = params.toString() ? `?${params.toString()}` : '';
      return oltId
        ? api.get<Page<Onu>>(`/olts/${encodeURIComponent(oltId)}/onus${qs}`)
        : api.get<Page<Onu>>(`/onus${qs}`);
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });

  const inScope = (p: string) => (port ? p === port : slot ? p.startsWith(`${slot}/`) : true);
  const unconfigured = (unconfQuery.data?.items ?? []).filter((u) => inScope(u.ponPort));

  const collate = (a: string, b: string) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

  const onus = (onusQuery.data?.items ?? [])
    .filter((o) => inScope(o.ponPort))
    .filter((o) => {
      switch (stateFilter) {
        case 'online':
          return o.state === 'working';
        // Offline excludes dormant so the two cards partition the down units.
        case 'offline':
          return o.state !== 'working' && !o.dormant;
        case 'dormant':
          return Boolean(o.dormant);
        default:
          return true; // 'all' (and 'unauthorised', which hides this list)
      }
    })
    .filter((o) => {
      if (modeFilter === 'all') return true;
      if (modeFilter === 'tr069_silent' || modeFilter === 'tr069_stale') {
        /*
          Read off the DTO rather than recomputed here. The judgement depends on
          the operator's online window, which lives in Settings and is known to
          the server; a browser-side copy of that number is the kind of second
          source this page has been bitten by before.
        */
        return o.tr069Reach === (modeFilter === 'tr069_silent' ? 'silent' : 'stale');
      }
      return modeOf(o) === modeFilter;
    })
    // Filtered here rather than server-side, unlike VLAN and model beside it:
    // `disabledAt` is already on every row, so this costs nothing and cannot
    // disagree with the pill the same row draws from the same field.
    .filter((o) =>
      serviceFilter === 'all'
        ? true
        : serviceFilter === 'disabled'
          ? Boolean(o.disabledAt)
          : !o.disabledAt,
    )
    // An ONU with no reading is not "normal" — it is unknown, and filtering for
    // a level should not quietly include the ones nobody has measured.
    .filter((o) => signalFilter === 'all' || signalLevel(o.rxPowerDbm) === signalFilter)
    .sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'number':
          // The URL number, which is allocation order — so sorting by it is
          // "oldest subscriber first", the same thing `added` means for ONUs
          // that carry an authorised-at. An unnumbered row sorts last either
          // way, like every other blank in this comparator.
          if (a.number === null || a.number === undefined) return 1;
          if (b.number === null || b.number === undefined) return -1;
          return dir * (a.number - b.number);
        case 'name':
          return dir * collate(a.name ?? '', b.name ?? '');
        case 'state':
          return dir * collate(a.state, b.state);
        case 'serial':
          return dir * collate(a.serial, b.serial);
        case 'model':
          // Sortable, and worth it: grouping the fleet by model is how an
          // operator sizes a firmware push or spots that one PON is all one
          // batch. Unknown sorts as '' — first ascending, last descending —
          // which is what Zone and Splitter beside it already do with a blank.
          return dir * collate(a.onuType ?? '', b.onuType ?? '');
        case 'location':
          // Port first, then the id within it, so a port's ONUs stay together
          // and read 1,2,10 rather than 1,10,2.
          return (
            dir *
            (collate(a.ponPort, b.ponPort) || Number(a.onuIndex ?? 0) - Number(b.onuIndex ?? 0))
          );
        case 'mode':
          return dir * collate(a.deviceId ? 'TR-069' : 'OMCI', b.deviceId ? 'TR-069' : 'OMCI');
        case 'lastSeen': {
          // Never heard from sorts last in both directions, like signal below:
          // an empty cell is not "longest ago".
          const at = lastHeard(a).at;
          const bt = lastHeard(b).at;
          if (at === null) return 1;
          if (bt === null) return -1;
          return dir * (at - bt);
        }
        case 'zone':
          return dir * collate(a.zoneName ?? '', b.zoneName ?? '');
        case 'splitter':
          return dir * collate(a.splitterName ?? '', b.splitterName ?? '');
        case 'added': {
          // When the unit came onto the platform: its authorisation, or — for
          // one adopted off the chassis, which has none — the moment its row
          // was made. Sorting on `authorizedAt` alone sank every adopted unit
          // below every authorised one: 184 of Mymanga's 201 have none, and
          // Report Desk (ZXICCD5CBCD9), adopted at 15:56 on 2026-10-07 beside
          // two neighbours authorised at 15:20 and 16:03, sat at row 122 while
          // they sat at 1 and 2 — "missing from the ONU list" (2026-10-07).
          const added = (o: Onu) => o.authorizedAt ?? o.createdAt ?? 0;
          return dir * (added(a) - added(b));
        }
        case 'signal':
        default:
          // No reading sorts last either way: an empty cell is not "worst".
          if (a.rxPowerDbm === null) return 1;
          if (b.rxPowerDbm === null) return -1;
          return dir * (a.rxPowerDbm - b.rxPowerDbm);
      }
    });
  /**
   * Whether this filtered set can be handed to a bulk plan, and why not.
   *
   * IDS are what travel, not the filter. The ONU list narrows by things the
   * bulk filter has no word for — signal band, in/out of service, TR-069
   * reachability — so translating the half that does map would build a plan
   * against MORE subscribers than the operator was looking at when they
   * pressed the button. The set on screen is the set that runs.
   *
   * Both refusals are stated rather than worked around. A run targets ONE
   * chassis (the schema requires it, and the API refuses foreign ids by name),
   * and a tick-list stops at `BULK_ONU_IDS_MAX` — truncating either would be a
   * bulk run against ONUs nobody chose.
   */
  const handoff = ((): { reason: string | null; oltId: string; onuIds: string[] } => {
    const chassis = [...new Set(onus.map((o) => o.oltId))];
    if (onus.length === 0) return { reason: 'No ONUs match these filters.', oltId: '', onuIds: [] };
    if (chassis.length > 1) {
      return {
        reason: `These ${onus.length} ONUs are on ${chassis.length} chassis. A bulk run targets one — choose an OLT above first.`,
        oltId: '',
        onuIds: [],
      };
    }
    if (onus.length > BULK_ONU_IDS_MAX) {
      return {
        reason: `${onus.length} is more than the ${BULK_ONU_IDS_MAX} a plan carries as an explicit list. Narrow the filters, or build the same selection on the bulk page.`,
        oltId: '',
        onuIds: [],
      };
    }
    return { reason: null, oltId: chassis[0]!, onuIds: onus.map((o) => o.id) };
  })();

  const scopeLabel = port ? ` on ${port}` : slot ? ` on card ${slot}` : '';

  // The migration dialog needs the full OLT object, not just an id. The
  // destination of a header "Move" is the selected OLT; a row's "Move here"
  // resolves its own OLT so it works even in the fleet-wide list.
  const oltById = (wantId: string) => olts.find((o) => o.id === wantId) ?? null;
  // Which chassis a card-level "Migrate" targets. A migration moves ONUs onto
  // ONE chassis, so this resolves to a single OLT: the one picked in the filter
  // above (so the dialog still opens to arm a window with nothing waiting), or
  // — when the list spans the fleet — the single chassis every waiting ONU
  // happens to sit on. A whole card turning up unconfigured on one OLT is the
  // common case, and it should not need the filter set first.
  const waitingOltIds = [...new Set(unconfigured.map((u) => u.oltId))];
  const migrationOlt =
    (oltId ? oltById(oltId) : null) ??
    (waitingOltIds.length === 1 ? oltById(waitingOltIds[0]!) : null);
  const openMigration = (row: UnconfiguredRow) => {
    const dest = oltById(row.oltId);
    if (dest) setMigrating(dest);
  };

  return (
    <div className="mt-5 space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            <CircuitBoard className="size-4 text-warn" />
            Awaiting authorisation
            <span className="text-xs font-normal text-faint">{unconfigured.length}</span>
          </span>
        }
        action={
          <div className="flex items-center gap-2">
            {can('olt.diagnose') && (
              <Button
                size="sm"
                variant="secondary"
                icon={<Shuffle className="size-3.5" />}
                disabled={!migrationOlt || Boolean(migrationOlt.disabledAt)}
                title={
                  migrationOlt
                    ? migrationOlt.disabledAt
                      ? `${migrationOlt.name} is out of service in SimpleACS. Enable it to move ONUs onto it.`
                      : `Move ONUs recorded on another port, card or OLT onto ${migrationOlt.name} — carrying each subscriber, its VLANs, its ports and its splitter chain. Preview first; nothing moves without you.`
                    : waitingOltIds.length > 1
                      ? `These ONUs are on ${waitingOltIds.length} chassis — choose an OLT above to migrate onto one at a time.`
                      : 'Choose an OLT above — a migration moves ONUs onto one chassis at a time.'
                }
                onClick={() => migrationOlt && setMigrating(migrationOlt)}
              >
                Migrate
              </Button>
            )}
            <Button
              size="sm"
              variant="secondary"
              icon={<RefreshCw className="size-3.5" />}
              loading={unconfQuery.isFetching}
              onClick={() => unconfQuery.refetch()}
            >
              Re-read
            </Button>
          </div>
        }
      >
        {unconfQuery.isLoading ? (
          <div className="flex items-center justify-center gap-3 p-8">
            <Spinner />
            <span className="text-sm text-muted">
              {oltId ? 'Reading the chassis…' : 'Reading every chassis…'}
            </span>
          </div>
        ) : unconfQuery.error ? (
          <div className="p-5">
            <ErrorNotice error={unconfQuery.error} />
          </div>
        ) : unconfigured.length === 0 ? (
          <EmptyState
            icon={<CircuitBoard className="size-8" />}
            title={`No ONUs waiting${scopeLabel}`}
            description={
              oltId
                ? 'Every ONU the chassis sees here is already authorised.'
                : 'Every ONU the OLTs can see is already authorised.'
            }
          />
        ) : (
          <Table>
            <THead>
              {!oltId && <th className="th">OLT</th>}
              <th className="th">Serial</th>
              <th className="th">PON port</th>
              <th className="th">Would become</th>
              <th className="th">State</th>
              <th className="th" />
            </THead>
            <TBody>
              {unconfigured.map((u) => (
                <TRow key={`${u.oltId}:${u.ponPort}:${u.serial}`}>
                  {!oltId && <td className="td text-muted">{u.oltName}</td>}
                  <td className="td font-mono text-xs">
                    <span className="flex items-center gap-2">
                      {u.serial}
                      {u.knownElsewhere && (
                        <span className="rounded bg-info-soft px-1.5 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-wide text-info">
                          Move
                        </span>
                      )}
                    </span>
                    {u.knownElsewhere && (
                      <span className="mt-0.5 block font-sans text-[11px] font-normal text-muted">
                        {u.knownElsewhere.name ?? 'Known subscriber'}
                        {u.knownElsewhere.number !== null && ` #${u.knownElsewhere.number}`} · was{' '}
                        {u.knownElsewhere.oltName} {u.knownElsewhere.ponPort}:
                        {u.knownElsewhere.onuIndex}
                        {u.knownElsewhere.removed && ' (retired record)'}
                      </span>
                    )}
                  </td>
                  <td className="td font-mono text-xs text-muted">{u.ponPort}</td>
                  <td className="td font-mono text-xs text-muted">
                    {u.ponPort}:{u.suggestedOnuId}
                  </td>
                  <td className="td">
                    <Pill tone="warn" dot>
                      {u.state}
                    </Pill>
                  </td>
                  <td className="td">
                    <div className="flex items-center justify-end gap-2">
                      {u.knownElsewhere && can('olt.diagnose') && (
                        <Button
                          size="sm"
                          variant="primary"
                          icon={<Shuffle className="size-3.5" />}
                          title={`Move ${u.serial} here, carrying its subscriber, VLANs, ports and splitter chain from ${u.knownElsewhere.oltName} ${u.knownElsewhere.ponPort}:${u.knownElsewhere.onuIndex}. You preview and confirm on the next screen.`}
                          onClick={() => openMigration(u)}
                        >
                          Move here
                        </Button>
                      )}
                      {can('onu.provision') && (
                        <Button
                          size="sm"
                          variant={u.knownElsewhere ? 'ghost' : 'primary'}
                          title={
                            u.knownElsewhere
                              ? 'Authorise this as a NEW subscriber instead — it will not carry the old record, its number or its history; a slot it still holds on this chassis is deauthorised first.'
                              : undefined
                          }
                          onClick={() =>
                            navigate(authorizePath(u), {
                              state: {
                                target: u,
                                vendor: olts.find((o) => o.id === u.oltId)?.vendor ?? null,
                                // The model and the server-resolved refusals
                                // ride along so the wizard gates from its
                                // first paint; it re-reads the OLT anyway.
                                model: olts.find((o) => o.id === u.oltId)?.model ?? null,
                                refusals: olts.find((o) => o.id === u.oltId)?.refusals,
                              },
                            })
                          }
                        >
                          {u.knownElsewhere ? 'Authorise afresh' : 'Authorise'}
                        </Button>
                      )}
                    </div>
                  </td>
                </TRow>
              ))}
            </TBody>
          </Table>
        )}
        {(unconfQuery.data?.warnings ?? []).map((w) => (
          <p key={w} className="border-t border-line px-5 py-2 text-xs text-warn">
            {w}
          </p>
        ))}
      </Card>

      {stateFilter !== 'unauthorised' && (
      <Card
        title={
          oltId
            ? `ONUs on ${oltName}${scopeLabel} (${onus.length})`
            : `ONUs across all OLTs (${onus.length})`
        }
        action={
          can('bulk.execute') ? (
            <Button
              variant="secondary"
              icon={<ListChecks className="size-4" />}
              disabled={handoff.reason !== null}
              title={handoff.reason ?? `Plan a bulk action against these ${onus.length}`}
              onClick={() =>
                navigate('/bulk-actions', {
                  state: { oltId: handoff.oltId, onuIds: handoff.onuIds },
                })
              }
            >
              Bulk actions ({onus.length})
            </Button>
          ) : null
        }
      >
        <form
          className="flex max-w-lg items-center gap-2 border-b border-line px-4 py-3"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(draft);
          }}
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Name, serial or interface…"
          />
          <Button type="submit" icon={<Search className="size-4" />}>
            Search
          </Button>
          {search && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setDraft('');
                setSearch('');
              }}
            >
              Clear
            </Button>
          )}
        </form>
        {onusQuery.isLoading ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : onus.length === 0 ? (
          <EmptyState
            icon={<Radio className="size-8" />}
            title="No configured ONUs here"
            description="Run Sync inventory on the OLT if you expect some."
          />
        ) : (
          <Table>
            <THead>
              {(
                [
                  ['number', '#'],
                  ['name', 'Name'],
                  ['state', 'State'],
                  ['serial', 'Serial'],
                  // Beside the serial: serial and model are one thought — which
                  // box is this — and reading them together is how a stock
                  // question gets answered without opening the ONU.
                  ['model', 'Model'],
                  ['location', 'Location'],
                  ['signal', 'Signal'],
                  ['mode', 'Mode'],
                  ['lastSeen', 'Last seen'],
                  ['zone', 'Zone'],
                  ['splitter', 'Splitter'],
                ] as Array<[SortKey, string]>
              ).map(([key, label]) => (
                <th key={key} className="th">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 hover:text-ink"
                    onClick={() => {
                      if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                      else {
                        setSortKey(key);
                        setSortDir('asc');
                      }
                    }}
                  >
                    {label}
                    {sortKey === key && <span aria-hidden>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                  </button>
                </th>
              ))}
            </THead>
            <TBody>
              {onus.map((onu) => {
                const band = signalBand(onu.rxPowerDbm);
                return (
                  <TRow
                    key={onu.id}
                    // One ONU page whatever manages it; the id resolves to the
                    // same record from either side.
                    onClick={() => navigate(onuPath(onu))}
                    className="cursor-pointer"
                  >
                    <td className="td text-sm tabular-nums text-muted">#{onu.number ?? '—'}</td>
                    <td className="td font-medium">
                      <span className="flex items-center gap-2">
                        <span className={onu.removedAt ? 'text-muted line-through' : undefined}>
                          {onu.name ?? '—'}
                        </span>
                        {onu.removedAt && (
                          <Pill
                            tone="warn"
                            className="shrink-0"
                          >
                            absent
                          </Pill>
                        )}
                        {/* The CPE's routed WAN holds no address: the customer
                            is off, and the platform says so here rather than
                            leaving it to the router's RADIUS log (2026-09-20). */}
                        {!onu.removedAt && onu.wanState === 'no_address' && (
                          <Pill
                            tone="danger"
                            className="shrink-0"
                            title="The linked CPE reports a routed WAN with no address — RADIUS rejecting its MAC, DHCP not answering, or PPPoE refused."
                          >
                            no WAN address
                          </Pill>
                        )}
                      </span>
                    </td>
                    <td className="td">
                      <Pill state tone={onuHealthTone(onu)} dot>
                        {onuHealthLabel(onu)}
                      </Pill>
                    </td>
                    <td className="td font-mono text-xs">
                      {onu.serial}
                      {/*
                        Marked, not columned. Two of the three states are
                        unremarkable and a whole column would spend width on
                        saying so 4,000 times; what an operator needs to see
                        without looking for it is the exception — an ONU that
                        appears managed and is not, because it answers to
                        somebody else's ACS.
                      */}
                      {modeOf(onu) === 'tr069_other' && (
                        <span
                          className="ml-1.5 align-middle"
                          title={`Informs an ACS this organization does not run${
                            onu.acsUrl ? ` — ${onu.acsUrl}` : ''
                          }. Only OMCI reaches it.`}
                        >
                          <Pill tone="warn">other ACS</Pill>
                        </span>
                      )}
                      {/*
                        Same shape and the same reason as the marker above it:
                        an ONU somebody took out of service still reads
                        `working` in the State column, because it is working —
                        what has stopped is SimpleACS acting on it, and that
                        is invisible until the operator plans a bulk run and
                        finds this subscriber missing from the count.
                      */}
                      {onu.disabledAt && (
                        <DisabledPill
                          kind="onu"
                          disabledReason={onu.disabledReason}
                          className="ml-1.5 align-middle"
                        />
                      )}
                    </td>
                    {/* What the chassis reported, verbatim. `546M` is shown as
                        `546M` even though it is an HG8546M written short —
                        rewriting it here would hide a real disagreement between
                        read paths behind a tidy column. Nothing reported at all
                        gets the same faint dash as an unmeasured signal, rather
                        than a blank cell that reads as a narrow column. */}
                    <td className="td text-xs">
                      {onu.onuType?.trim() ? (
                        onu.onuType
                      ) : (
                        <span className="text-faint" title={ONU_MODEL_NONE_LABEL}>
                          —
                        </span>
                      )}
                    </td>
                    {/* Port and id are one address — `1/3/15:11` is how the
                        chassis names this subscriber and how every command
                        targets it, so it reads as one thing. */}
                    <td className="td font-mono text-xs text-muted">
                      {onu.ponPort ? `${onu.ponPort}:${onu.onuIndex}` : '—'}
                    </td>
                    <td className="td">
                      {onu.rxPowerDbm === null ? (
                        <span className="text-faint">—</span>
                      ) : (
                        <Pill state tone={band.tone}>{onu.rxPowerDbm} dBm</Pill>
                      )}
                    </td>
                    {/* Through the same `modeOf` the filter uses, so the cell
                        and the dropdown cannot disagree — and so an ONU on
                        somebody else's ACS is not filed under plain TR-069. */}
                    <td className="td">
                      {(() => {
                        const mode = modeOf(onu);
                        const mismatch = onu.oltAcsMismatch === true;
                        return (
                          <span
                            title={
                              mismatch
                                ? 'Informing our ACS, but the OLT config has no TR-069 line for it — resync the ONU to write it.'
                                : undefined
                            }
                          >
                            <Pill
                              // A category (TR-069 / OMCI) that follows position, except
                              // when it flags a problem, which keeps its amber.
                              state={mismatch || mode === 'tr069_other'}
                              tone={
                                mismatch
                                  ? 'warn'
                                  : mode === 'tr069'
                                    ? 'primary'
                                    : mode === 'tr069_other'
                                      ? 'warn'
                                      : 'neutral'
                              }
                            >
                              {mode === 'omci'
                                ? 'OMCI'
                                : mode === 'tr069_other'
                                  ? 'TR-069 (other)'
                                  : 'TR-069'}
                              {mismatch ? ' ⚠' : ''}
                            </Pill>
                          </span>
                        );
                      })()}
                    </td>
                    {/* Last heard from, whichever half of the platform heard it.
                        The source is named because the two differ: the OLT
                        figure only moves on a sweep that found the ONU working,
                        which is what made live subscribers read as last seen
                        hours ago. */}
                    <td className="td whitespace-nowrap text-xs text-muted">
                      {(() => {
                        const heard = lastHeard(onu);
                        if (heard.at === null) {
                          return (
                            <span className="text-faint" title="Neither the ACS nor the OLT has reported this ONU yet.">
                              never
                            </span>
                          );
                        }
                        return (
                          <span
                            title={`${formatDateTime(heard.at, { seconds: true })} — from ${
                              heard.via === 'ACS'
                                ? 'the ACS, when the CPE last informed'
                                : 'the OLT sweep, which only advances on a reading that found it working'
                            }`}
                          >
                            {formatRelative(heard.at)}
                            <span className="ml-1 text-faint">{heard.via}</span>
                          </span>
                        );
                      })()}
                    </td>
                    {/* A blank cell states a fact and offers nothing. The
                        moment somebody notices a subscriber has no placement is
                        while scanning this list, so the fix is offered here. */}
                    <td className="td text-muted">
                      {onu.zoneName || <SetPlacement onClick={() => setPlacing(onu)} />}
                    </td>
                    <td className="td text-muted">
                      {onu.splitterName ? (
                        <>
                          {onu.splitterName}
                          {onu.splitterPort ? (
                            <span className="text-faint"> · port {onu.splitterPort}</span>
                          ) : null}
                        </>
                      ) : (
                        <SetPlacement onClick={() => setPlacing(onu)} />
                      )}
                    </td>
                  </TRow>
                );
              })}
            </TBody>
          </Table>
        )}
      </Card>
      )}

      {placing && (
        <PlacementDialog
          onuId={placing.id}
          label={placing.name || placing.serial}
          currentZoneId={placing.zoneId}
          currentSplitterId={placing.splitterId}
          onClose={() => setPlacing(null)}
        />
      )}

      {migrating && (
        <OnuMigrationDialog
          olt={migrating}
          onClose={() => setMigrating(null)}
          // The moved ONUs leave the waiting list and appear in inventory; the
          // shared olts/onus keys the task already refreshes cover inventory,
          // and this adds the awaiting card's own read.
          invalidate={[['unconfigured']]}
        />
      )}
    </div>
  );
}
